import React from 'react';
import { Check, X, MapPin, Bed, Calendar, Sparkle, Tag } from '@phosphor-icons/react';
import RichContent from './RichContent';

// Brand logo — same SVG as the rest of the app
const BrandLogo = ({ height = 28, color = '#0F1B3D', arcColor = '#FB7185' }) => (
    <svg viewBox="0 0 800 375" style={{ height: `${height}px`, width: 'auto' }} aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
        <path d="M 165 245 Q 400 110 635 245" fill="none" stroke={arcColor} strokeWidth="10" strokeLinecap="round" />
        <text x="400" y="340" textAnchor="middle" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="92" fontWeight="700" fill={color} letterSpacing="-2.5">exploremos</text>
    </svg>
);

/**
 * Live preview of a Travel Certificate.
 * Supports 3 layouts: certificate (formal), voucher (coupon), card (modern card)
 */
const CertificatePreview = ({ data, scale = 1 }) => {
    const d = data?.design || {};
    const layout = d.layout_style || 'certificate';
    const titleFont = d.title_font || 'Plus Jakarta Sans';
    const bodyFont = d.subtitle_font || 'Inter';
    const subtitleFont = d.subtitle_font || bodyFont;
    const bodyTextFont = d.body_font || bodyFont;
    const labelFont = d.label_font || bodyFont;
    const accent = d.accent_color || '#FB7185';
    const titleColor = d.title_color || '#1F2D4B';
    const bodyColor = d.subtitle_color || '#4B5878';
    const bg = d.background_color || '#FFFFFF';
    const bgImage = d.background_image;
    const borderStyle = d.border_style || 'elegant';

    // font sizes (px)
    const titleSize = d.title_size || 28;
    const subtitleSize = d.subtitle_size || 14;
    const bodySize = d.body_size || 12;
    const labelSize = d.label_size || 10;

    // Per-text typography (weight + italic)
    const titleWeight = d.title_weight ?? 700;
    const subtitleWeight = d.subtitle_weight ?? 400;
    const bodyWeight = d.body_weight ?? 400;
    const labelWeight = d.label_weight ?? 700;
    const titleStyle = d.title_italic ? 'italic' : 'normal';
    const subtitleStyle = d.subtitle_italic ? 'italic' : 'normal';
    const bodyStyle = d.body_italic ? 'italic' : 'normal';
    const labelStyle = d.label_italic ? 'italic' : 'normal';

    // Reusable style builders
    const titleS = { fontFamily: titleFont, color: titleColor, fontSize: `${titleSize}px`, fontWeight: titleWeight, fontStyle: titleStyle, letterSpacing: '-0.01em' };
    const subtitleS = { fontFamily: subtitleFont, color: bodyColor, fontSize: `${subtitleSize}px`, fontWeight: subtitleWeight, fontStyle: subtitleStyle };
    const bodyS = { fontFamily: bodyTextFont, color: bodyColor, fontSize: `${bodySize}px`, fontWeight: bodyWeight, fontStyle: bodyStyle };
    const labelS = { fontFamily: labelFont, color: accent, fontSize: `${labelSize}px`, fontWeight: labelWeight, fontStyle: labelStyle, letterSpacing: '0.18em', textTransform: 'uppercase' };
    const labelMutedS = { fontFamily: labelFont, color: '#8B8474', fontSize: `${labelSize - 1}px`, fontWeight: labelWeight, fontStyle: labelStyle, letterSpacing: '0.1em', textTransform: 'uppercase' };

    const sampleFolio = `${data?.code_prefix || 'XX'}-260327-0001`;

    const containerBorder =
        borderStyle === 'elegant' ? 'rounded-2xl shadow-xl border border-[#ECE3D2]' :
        borderStyle === 'modern' ? 'rounded-lg shadow-lg border-2 border-[#1F2D4B]' :
        borderStyle === 'voucher' ? 'rounded-lg shadow-md border-2 border-dashed border-[#8B8474]' :
        'rounded-xl shadow-md border border-[#ECE3D2]';

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 0
    }).format(amount || 0);

    // ========== LAYOUT 1: CERTIFICATE (formal, vertical) ==========
    if (layout === 'certificate') {
        return (
            <div
                className={`bg-white overflow-hidden ${containerBorder}`}
                style={{ fontFamily: bodyFont, backgroundColor: bg }}
            >
                {/* Image top */}
                <div
                    className="relative w-full aspect-[4/3] bg-[#1F2D4B]"
                    style={{
                        backgroundImage: bgImage ? `url(${bgImage})` : 'linear-gradient(135deg, #1F2D4B 0%, #2E3F60 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded" style={{ padding: '3px 6px' }}>
                        <BrandLogo height={28} color="#0F1B3D" arcColor={accent} />
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded px-2 py-1 font-mono-brand text-[10px] font-semibold text-[#1F2D4B]">
                        {sampleFolio}
                    </div>
                </div>

                {/* Content panel */}
                <div className="p-6">
                    <p className="mb-2" style={labelS}>
                        {data?.title || 'Certificado de viaje'}
                    </p>

                    <h2 className="leading-tight mb-2" style={titleS}>
                        {data?.name || 'Nombre del producto'}
                    </h2>

                    {data?.subtitle && (
                        <p style={subtitleS} className="mb-3 leading-snug">
                            {data.subtitle}
                        </p>
                    )}

                    {data?.brief_description && (
                        <RichContent
                            value={data.brief_description}
                            style={bodyS}
                            className="leading-relaxed mb-4"
                        />
                    )}

                    {/* divider */}
                    <div className="flex items-center gap-2 my-4">
                        <div className="flex-1 h-px bg-[#ECE3D2]" />
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />
                        <div className="flex-1 h-px bg-[#ECE3D2]" />
                    </div>

                    {/* destination + price grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <p className="font-bold uppercase mb-0.5" style={{ color: '#8B8474', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                Destino
                            </p>
                            <p style={{ fontFamily: titleFont, color: titleColor, fontSize: `${subtitleSize + 2}px`, fontWeight: 600 }}>
                                {data?.destination || 'Sin destino'}
                            </p>
                        </div>
                        <div>
                            <p className="font-bold uppercase mb-0.5" style={{ color: '#8B8474', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                Vigencia
                            </p>
                            <p style={{ color: titleColor, fontSize: `${subtitleSize + 2}px`, fontWeight: 600 }}>
                                {data?.validity_days || 365} días
                            </p>
                        </div>
                    </div>

                    {data?.full_description && (
                        <div className="mb-4 pt-3 border-t border-[#ECE3D2]">
                            <p className="font-bold uppercase mb-1.5" style={{ color: '#8B8474', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                Descripción
                            </p>
                            <RichContent
                                value={data.full_description}
                                style={bodyS}
                                className="leading-relaxed"
                            />
                        </div>
                    )}

                    {/* Includes */}
                    {data?.includes?.length > 0 && (
                        <div className="mb-3 pt-3 border-t border-[#ECE3D2]">
                            <p className="font-bold uppercase mb-2 flex items-center gap-1" style={{ color: '#2D7A52', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                <Check size={11} weight="bold" /> Incluye
                            </p>
                            <ul className="space-y-1">
                                {data.includes.map((item, i) => (
                                    <li key={i} className="flex items-start gap-1.5" style={{ color: bodyColor, fontSize: `${bodySize}px` }}>
                                        <Check size={10} weight="bold" className="text-[#2D7A52] mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Excludes */}
                    {data?.excludes?.length > 0 && (
                        <div className="mb-3 pt-3 border-t border-[#ECE3D2]">
                            <p className="font-bold uppercase mb-2 flex items-center gap-1" style={{ color: '#C8413A', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                <X size={11} weight="bold" /> No incluye
                            </p>
                            <ul className="space-y-1">
                                {data.excludes.map((item, i) => (
                                    <li key={i} className="flex items-start gap-1.5" style={{ color: bodyColor, fontSize: `${bodySize}px` }}>
                                        <X size={10} weight="bold" className="text-[#C8413A] mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {data?.how_to_book && (
                        <div className="mb-3 pt-3 border-t border-[#ECE3D2]">
                            <p className="font-bold uppercase mb-1.5" style={{ color: accent, fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                Cómo reservar
                            </p>
                            <RichContent value={data.how_to_book} style={bodyS} className="leading-relaxed" />
                        </div>
                    )}

                    {/* Price footer */}
                    <div className="flex items-end justify-between pt-4 border-t border-[#ECE3D2] mt-4">
                        <div>
                            <p className="font-bold uppercase mb-0.5" style={{ color: '#8B8474', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                Valor
                            </p>
                            <p className="font-bold" style={{ fontFamily: titleFont, color: titleColor, fontSize: `${titleSize - 4}px`, letterSpacing: '-0.01em' }}>
                                {formatCurrency(data?.price)}
                            </p>
                        </div>
                        {data?.original_price && data.original_price > data.price && (
                            <div className="text-right">
                                <p className="font-bold uppercase mb-0.5" style={{ color: '#8B8474', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>
                                    Antes
                                </p>
                                <p className="line-through" style={{ color: '#8B8474', fontSize: `${subtitleSize}px` }}>
                                    {formatCurrency(data.original_price)}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Brand footer */}
                    <div className="mt-5 pt-4 border-t border-[#ECE3D2] flex flex-col items-center gap-1.5">
                        <BrandLogo height={26} color={titleColor} arcColor={accent} />
                        <p className="text-[9px] uppercase tracking-[0.2em]" style={{ color: '#8B8474' }}>
                            Viajar · para volver a ti
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ========== LAYOUT 2: VOUCHER (coupon, perforated) ==========
    if (layout === 'voucher') {
        return (
            <div className={`bg-white overflow-hidden relative ${containerBorder}`} style={{ fontFamily: bodyFont }}>
                {/* Punch dots top */}
                <div className="absolute -left-2 top-1/3 w-4 h-4 rounded-full bg-[#F4ECE0]" />
                <div className="absolute -right-2 top-1/3 w-4 h-4 rounded-full bg-[#F4ECE0]" />

                {/* Top: Image strip */}
                <div
                    className="relative w-full h-32 bg-[#1F2D4B]"
                    style={{
                        backgroundImage: bgImage ? `url(${bgImage})` : 'linear-gradient(135deg, #1F2D4B 0%, #2E3F60 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded" style={{ padding: '3px 6px' }}>
                        <BrandLogo height={28} color="#0F1B3D" arcColor={accent} />
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded px-2 py-1 font-mono-brand text-[10px] font-semibold text-[#1F2D4B]">
                        {sampleFolio}
                    </div>
                    <div className="absolute bottom-3 left-4 right-4">
                        <p className="mb-1" style={{ ...labelS, color: accent }}>
                            {data?.title || 'Cupón de viaje'}
                        </p>
                        <h2 className="text-white leading-tight" style={{ ...titleS, color: '#FFFFFF', fontSize: `${titleSize - 4}px` }}>
                            {data?.name || 'Nombre del producto'}
                        </h2>
                    </div>
                </div>

                {/* Dashed separator */}
                <div className="border-t-2 border-dashed border-[#ECE3D2] mx-4" />

                {/* Body */}
                <div className="p-5">
                    {data?.subtitle && (
                        <p style={subtitleS} className="mb-3 leading-snug">
                            {data.subtitle}
                        </p>
                    )}

                    {data?.brief_description && (
                        <RichContent
                            value={data.brief_description}
                            style={bodyS}
                            className="leading-relaxed mb-4"
                        />
                    )}

                    <div className="grid grid-cols-2 gap-3 py-3 my-3 bg-[#FAF6F0] rounded px-3">
                        <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-[#8B8474]" />
                            <div>
                                <p style={{ color: '#8B8474', fontSize: `${labelSize - 1}px` }}>Destino</p>
                                <p style={{ color: titleColor, fontSize: `${subtitleSize}px`, fontWeight: 600 }}>{data?.destination || '—'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Bed size={14} className="text-[#8B8474]" />
                            <div>
                                <p style={{ color: '#8B8474', fontSize: `${labelSize - 1}px` }}>Noches</p>
                                <p style={{ color: titleColor, fontSize: `${subtitleSize}px`, fontWeight: 600 }}>{data?.nights || 0}</p>
                            </div>
                        </div>
                    </div>

                    {data?.full_description && (
                        <RichContent value={data.full_description} style={bodyS} className="leading-relaxed mb-3" />
                    )}

                    {data?.includes?.length > 0 && (
                        <div className="mb-3">
                            <p className="font-bold uppercase mb-1.5" style={{ color: '#2D7A52', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>✓ Incluye</p>
                            <p style={{ color: bodyColor, fontSize: `${bodySize}px` }}>{data.includes.join(' · ')}</p>
                        </div>
                    )}

                    {data?.excludes?.length > 0 && (
                        <div className="mb-3">
                            <p className="font-bold uppercase mb-1.5" style={{ color: '#C8413A', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>✗ No incluye</p>
                            <p style={{ color: bodyColor, fontSize: `${bodySize}px` }}>{data.excludes.join(' · ')}</p>
                        </div>
                    )}

                    {data?.how_to_book && (
                        <div className="mb-3 p-3 rounded bg-[#FFF8E7] border border-[#F5DEB3]">
                            <p className="font-bold uppercase mb-1" style={{ color: '#92400E', fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>Cómo reservar</p>
                            <RichContent value={data.how_to_book} style={{ ...bodyS, color: '#92400E' }} className="leading-relaxed" />
                        </div>
                    )}

                    {/* Big code at bottom */}
                    <div className="border-t-2 border-dashed border-[#ECE3D2] pt-3 mt-3 flex items-end justify-between">
                        <div>
                            <p style={{ color: '#8B8474', fontSize: `${labelSize - 1}px` }} className="uppercase font-bold tracking-wider">Valor</p>
                            <p className="font-bold" style={{ fontFamily: titleFont, color: titleColor, fontSize: `${titleSize}px` }}>
                                {formatCurrency(data?.price)}
                            </p>
                        </div>
                        <div className="text-right">
                            <p style={{ color: '#8B8474', fontSize: `${labelSize - 1}px` }} className="uppercase font-bold tracking-wider">Folio</p>
                            <p className="font-mono-brand font-bold" style={{ color: titleColor, fontSize: `${subtitleSize}px` }}>{sampleFolio}</p>
                        </div>
                    </div>

                    {/* Brand footer */}
                    <div className="mt-4 pt-3 border-t border-[#ECE3D2] flex flex-col items-center gap-1">
                        <BrandLogo height={22} color={titleColor} arcColor={accent} />
                        <p className="text-[9px] uppercase tracking-[0.2em]" style={{ color: '#8B8474' }}>
                            Viajar · para volver a ti
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ========== LAYOUT 3: CARD (modern, image-bg full, text overlay) ==========
    return (
        <div
            className={`relative overflow-hidden ${containerBorder}`}
            style={{
                fontFamily: bodyFont,
                backgroundImage: bgImage
                    ? `linear-gradient(to bottom, rgba(31,45,75,0.1) 0%, rgba(31,45,75,0.95) 100%), url(${bgImage})`
                    : 'linear-gradient(135deg, #1F2D4B 0%, #2E3F60 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '550px'
            }}
        >
            {/* Top: brand logo (left) + folio (right) */}
            <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                <div className="bg-white/95 backdrop-blur-sm rounded" style={{ padding: '3px 6px' }}>
                    <BrandLogo height={28} color="#0F1B3D" arcColor={accent} />
                </div>
                <div className="bg-white/95 backdrop-blur-sm rounded px-2 py-1 font-mono-brand text-[10px] font-semibold text-[#1F2D4B]">
                    {sampleFolio}
                </div>
            </div>

            {/* Bottom content over dark gradient */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="mb-2" style={labelS}>
                    {data?.title || 'Certificado'}
                </p>
                <h2 className="leading-tight mb-3 text-white" style={{ ...titleS, color: '#FFFFFF' }}>
                    {data?.name || 'Nombre del producto'}
                </h2>
                {data?.subtitle && (
                    <p style={{ ...subtitleS, color: 'rgba(255,255,255,0.85)' }} className="mb-3 leading-snug">
                        {data.subtitle}
                    </p>
                )}
                {data?.brief_description && (
                    <RichContent
                        value={data.brief_description}
                        style={{ ...bodyS, color: 'rgba(255,255,255,0.75)' }}
                        className="mb-4 leading-relaxed"
                    />
                )}

                <div className="flex flex-wrap gap-3 mb-4 text-white/90">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded text-xs">
                        <MapPin size={11} weight="bold" /> {data?.destination || 'Sin destino'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded text-xs">
                        <Bed size={11} weight="bold" /> {data?.nights || 0}N
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded text-xs">
                        <Calendar size={11} weight="bold" /> {data?.validity_days || 365}d
                    </span>
                </div>

                {data?.includes?.length > 0 && (
                    <div className="mb-3">
                        <p className="text-white/60 uppercase font-bold mb-1" style={{ fontSize: `${labelSize - 1}px`, letterSpacing: '0.1em' }}>Incluye</p>
                        <p className="text-white/90" style={{ fontSize: `${bodySize}px` }}>{data.includes.slice(0, 4).join(' · ')}</p>
                    </div>
                )}

                <div className="pt-3 mt-3 border-t border-white/15 flex items-end justify-between">
                    <p className="text-white font-bold" style={{ fontFamily: titleFont, fontSize: `${titleSize - 2}px`, letterSpacing: '-0.01em' }}>
                        {formatCurrency(data?.price)}
                    </p>
                    {data?.original_price && data.original_price > data.price && (
                        <p className="text-white/50 line-through" style={{ fontSize: `${subtitleSize}px` }}>
                            {formatCurrency(data.original_price)}
                        </p>
                    )}
                </div>

                {/* Brand mark */}
                <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-center gap-2">
                    <BrandLogo height={20} color="#FFFFFF" arcColor={accent} />
                    <span className="text-white/40 text-[9px] uppercase tracking-[0.2em] ml-1">Viajar · para volver a ti</span>
                </div>
            </div>
        </div>
    );
};

export default CertificatePreview;
